import { Sidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { fetchConversations } from "@/lib/api";
import type { ConversationSummary } from "@/lib/types";
import { useUser } from "@/lib/useUser";
import { cn } from "@/lib/utils";
import { Compass, Menu, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";

export function Layout() {
  const { user, loading, signOut } = useUser();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    if (!user) return;
    const list = await fetchConversations();
    setConversations(list);
  }, [user]);

  useEffect(() => {
    reload();
  }, [reload, location.pathname]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="grid h-screen w-screen place-items-center text-muted-foreground">
        <div className="flex items-center gap-2 text-sm">
          <div className="size-1.5 animate-pulse rounded-full bg-primary" />
          loading
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  return (
    <div className="flex h-screen w-screen overflow-hidden">
      {open ? (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm md:hidden"
        />
      ) : null}

      <Sidebar
        conversations={conversations}
        user={user}
        onSignOut={signOut}
        open={open}
        onClose={() => setOpen(false)}
      />

      <main className="scrollbar-thin relative flex-1 overflow-y-auto">
        <header
          className={cn(
            "sticky top-0 z-20 flex items-center justify-between gap-2 border-b border-border/40 bg-background/85 px-3 py-2 backdrop-blur md:hidden",
          )}
        >
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="size-5" />
          </Button>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5"
          >
            <div className="grid size-6 place-items-center rounded-md bg-primary/15 ring-1 ring-primary/30">
              <Compass className="size-3.5 text-primary" strokeWidth={2.4} />
            </div>
            <span className="font-serif text-base italic tracking-tight">
              sanplexity
            </span>
          </button>
          <span className="size-7" />
        </header>

        <Outlet context={{ reloadConversations: reload }} />
      </main>
    </div>
  );
}

export type LayoutContext = {
  reloadConversations: () => Promise<void>;
};
